import { tenantStorageKey } from './tenantStorage.ts'
import type { InstalledSystemInfo } from './serverSystemInfo'
import type { ServerOption } from './serverContext.tsx'
import type { SystemStats } from '../hooks/useWebSocket.ts'

const SERVER_INVENTORY_KEY = 'servernoxOfflineServerInventory'
const DASHBOARD_KEY = 'servernoxOfflineDashboard'
const AUTH_CONTEXT_KEY = 'servernoxOfflineAuthContext'
const CACHE_VERSION = 1 as const
const MAX_CACHE_BYTES = 256 * 1024
const DASHBOARD_INFO_FIELDS = new Set(['hostname', 'os', 'os_pretty_name', 'platform_version', 'os_codename', 'platform', 'distro', 'version', 'kernel', 'arch', 'uptime', 'cpu_model', 'cpu_cores'])
const DASHBOARD_COUNT_FIELDS = new Set(['websites', 'sslTotal', 'sslInvalid', 'pm2Total', 'pm2Online', 'networkTotal', 'networkUp'])

export interface OfflineServerInventory {
  version: typeof CACHE_VERSION
  updatedAt: number
  servers: ServerOption[]
}

export interface OfflineServiceStatus {
  running: boolean
  installed?: boolean
  detail?: string
}

export interface OfflineDashboardSnapshot {
  version: typeof CACHE_VERSION
  updatedAt: number
  serverName: string
  info?: Record<string, string | number | boolean | null>
  services?: Record<string, OfflineServiceStatus>
  counts?: {
    websites?: number
    sslTotal?: number
    sslInvalid?: number
    pm2Total?: number
    pm2Online?: number
    networkTotal?: number
    networkUp?: number
  }
  stats?: SystemStats
}

export interface OfflineAuthContext {
  version: typeof CACHE_VERSION
  updatedAt: number
  roles: string[]
  capabilities: string[]
  organization: { id: string; name: string; slug: string; kind: 'personal' | 'team'; plan: string } | null
  organizations: Array<{ id: string; name: string; slug: string; kind: 'personal' | 'team'; plan: string }>
  is_platform_admin: boolean
}

type CacheStorage = Pick<Storage, 'getItem'>

function safeText(value: unknown, max = 240): string {
  return typeof value === 'string' ? value.trim().slice(0, max) : ''
}

function safeTimestamp(value: unknown): number {
  const timestamp = Number(value)
  return Number.isFinite(timestamp) && timestamp > 0 && timestamp <= Date.now() + 300_000 ? timestamp : 0
}

function safeSystemInfo(value: unknown): InstalledSystemInfo | undefined {
  if (!value || typeof value !== 'object') return undefined
  const row = value as Record<string, unknown>
  const name = safeText(row.name)
  if (!name) return undefined
  return { name, checked_at: safeText(row.checked_at, 64), platform: safeText(row.platform), version: safeText(row.version), codename: safeText(row.codename), kernel: safeText(row.kernel), arch: safeText(row.arch, 64) }
}

function safeServer(value: unknown): ServerOption | null {
  if (!value || typeof value !== 'object') return null
  const row = value as Record<string, unknown>
  const id = safeText(row.id, 128)
  const name = safeText(row.name, 160)
  if (!id || !name) return null
  const status = ['ok', 'offline', 'installing', 'provisioning', 'failed'].includes(String(row.status))
    ? row.status as ServerOption['status']
    : 'ok'
  const features = Array.isArray(row.features)
    ? row.features.filter((item): item is string => typeof item === 'string').slice(0, 300)
    : undefined
  const executables = row.executables && typeof row.executables === 'object'
    ? Object.fromEntries(Object.entries(row.executables as Record<string, unknown>)
      .filter(([, enabled]) => typeof enabled === 'boolean').slice(0, 300)) as Record<string, boolean>
    : undefined
  const unavailableFeatures = row.unavailable_features && typeof row.unavailable_features === 'object'
    ? Object.fromEntries(Object.entries(row.unavailable_features as Record<string, unknown>)
      .filter(([, reason]) => typeof reason === 'string').slice(0, 300)
      .map(([key, reason]) => [key, String(reason).slice(0, 240)]))
    : undefined
  return {
    id, name, host: safeText(row.host, 255), is_hub: row.is_hub === true,
    // A cached inventory never claims current reachability. The UI labels the
    // row as last-known data instead of rendering a live red/green verdict.
    reachable: false, status,
    transport: safeText(row.transport, 32) || undefined,
    wg_ip: safeText(row.wg_ip, 64) || undefined,
    wireguard_available: typeof row.wireguard_available === 'boolean' ? row.wireguard_available : undefined,
    wireguard_unavailable_reason: safeText(row.wireguard_unavailable_reason, 240) || undefined,
    os: safeText(row.os, 160) || undefined,
    os_info: safeSystemInfo(row.os_info),
    arch: safeText(row.arch, 64) || undefined,
    features, executables, unavailable_features: unavailableFeatures,
    stats: row.stats && typeof row.stats === 'object' ? {
      cpu_percent: typeof (row.stats as any).cpu_percent === 'number' ? (row.stats as any).cpu_percent : undefined,
      mem_percent: typeof (row.stats as any).mem_percent === 'number' ? (row.stats as any).mem_percent : undefined,
      mem_used_gb: typeof (row.stats as any).mem_used_gb === 'number' ? (row.stats as any).mem_used_gb : undefined,
      mem_total_gb: typeof (row.stats as any).mem_total_gb === 'number' ? (row.stats as any).mem_total_gb : undefined,
    } : undefined,
  }
}

function safeStats(value: unknown): SystemStats | undefined {
  if (!value || typeof value !== 'object') return undefined
  const row = value as Record<string, unknown>
  const fields = ['cpu_percent', 'mem_percent', 'mem_used_gb', 'mem_total_gb', 'disk_percent', 'disk_used_gb', 'disk_total_gb', 'net_recv_kb', 'net_sent_kb'] as const
  const numbers = Object.fromEntries(fields.map(field => [field, Number(row[field])])) as unknown as Omit<SystemStats, 'timestamp'>
  if (fields.some(field => !Number.isFinite(numbers[field]))) return undefined
  return { ...numbers, timestamp: safeText(row.timestamp, 64) || new Date().toISOString() }
}

export function readOfflineServerInventory(storage: CacheStorage = localStorage): OfflineServerInventory | null {
  try {
    const raw = storage.getItem(tenantStorageKey(SERVER_INVENTORY_KEY, storage))
    if (!raw || raw.length > MAX_CACHE_BYTES) return null
    const parsed = JSON.parse(raw) as Record<string, unknown>
    if (parsed.version !== CACHE_VERSION) return null
    const updatedAt = safeTimestamp(parsed.updatedAt)
    const servers = Array.isArray(parsed.servers)
      ? parsed.servers.map(safeServer).filter((server): server is ServerOption => server !== null).slice(0, 200)
      : []
    return updatedAt ? { version: CACHE_VERSION, updatedAt, servers } : null
  } catch { return null }
}

export function writeOfflineServerInventory(servers: ServerOption[], storage: Storage = localStorage, preferredServer = ''): OfflineServerInventory {
  // Keep the selected server even when a large fleet exceeds the bounded cache.
  const preferred = servers.find(server => server.name === preferredServer)
  const ordered = preferred ? [preferred, ...servers.filter(server => server !== preferred)] : servers
  const value: OfflineServerInventory = {
    version: CACHE_VERSION,
    updatedAt: Date.now(),
    servers: ordered.map(safeServer).filter((server): server is ServerOption => server !== null).slice(0, 200),
  }
  try { storage.setItem(tenantStorageKey(SERVER_INVENTORY_KEY, storage), JSON.stringify(value)) } catch {}
  return value
}

function dashboardKey(serverName: string, storage: CacheStorage): string {
  return tenantStorageKey(`${DASHBOARD_KEY}:${safeText(serverName, 160)}`, storage)
}

export function readOfflineDashboard(serverName: string, storage: CacheStorage = localStorage): OfflineDashboardSnapshot | null {
  if (!serverName) return null
  try {
    const raw = storage.getItem(dashboardKey(serverName, storage))
    if (!raw || raw.length > MAX_CACHE_BYTES) return null
    const parsed = JSON.parse(raw) as OfflineDashboardSnapshot
    if (parsed.version !== CACHE_VERSION) return null
    const updatedAt = safeTimestamp(parsed.updatedAt)
    if (!updatedAt || safeText(parsed.serverName, 160) !== serverName) return null
    const services = parsed.services && typeof parsed.services === 'object'
      ? Object.fromEntries(Object.entries(parsed.services).slice(0, 20).map(([key, value]) => [safeText(key, 64), {
        running: value?.running === true,
        installed: typeof value?.installed === 'boolean' ? value.installed : undefined,
        detail: safeText(value?.detail, 160) || undefined,
      }]))
      : undefined
    const counts = parsed.counts && typeof parsed.counts === 'object'
      ? Object.fromEntries(Object.entries(parsed.counts).filter(([key, value]) => DASHBOARD_COUNT_FIELDS.has(key) && Number.isFinite(Number(value))).map(([key, value]) => [key, Math.max(0, Number(value))]))
      : undefined
    const info = parsed.info && typeof parsed.info === 'object'
      ? Object.fromEntries(Object.entries(parsed.info).filter(([key, value]) => DASHBOARD_INFO_FIELDS.has(key) && (['string', 'number', 'boolean'].includes(typeof value) || value === null)).map(([key, value]) => [key, typeof value === 'string' ? value.slice(0, 240) : value]))
      : undefined
    return { version: CACHE_VERSION, updatedAt, serverName, services, counts, info, stats: safeStats(parsed.stats) }
  } catch { return null }
}

export function mergeOfflineDashboard(serverName: string, patch: Partial<OfflineDashboardSnapshot>, storage: Storage = localStorage): OfflineDashboardSnapshot | null {
  if (!serverName) return null
  const current = readOfflineDashboard(serverName, storage)
  const safeInfo = patch.info && typeof patch.info === 'object'
    ? Object.fromEntries(Object.entries(patch.info).filter(([key, value]) => DASHBOARD_INFO_FIELDS.has(key) && (['string', 'number', 'boolean'].includes(typeof value) || value === null)).map(([key, value]) => [key, typeof value === 'string' ? value.slice(0, 240) : value]))
    : {}
  const safeCounts = patch.counts && typeof patch.counts === 'object'
    ? Object.fromEntries(Object.entries(patch.counts).filter(([key, value]) => DASHBOARD_COUNT_FIELDS.has(key) && Number.isFinite(Number(value))).map(([key, value]) => [key, Math.max(0, Number(value))]))
    : {}
  const safeServices = patch.services && typeof patch.services === 'object'
    ? Object.fromEntries(Object.entries(patch.services).slice(0, 20).map(([key, value]) => [safeText(key, 64), {
      running: value?.running === true,
      installed: typeof value?.installed === 'boolean' ? value.installed : undefined,
      detail: safeText(value?.detail, 160) || undefined,
    }]))
    : {}
  const candidate: OfflineDashboardSnapshot = {
    version: CACHE_VERSION, updatedAt: Date.now(), serverName,
    info: { ...(current?.info || {}), ...safeInfo },
    services: { ...(current?.services || {}), ...safeServices },
    counts: { ...(current?.counts || {}), ...safeCounts },
    stats: safeStats(patch.stats) || current?.stats,
  }
  try {
    storage.setItem(dashboardKey(serverName, storage), JSON.stringify(candidate))
    return readOfflineDashboard(serverName, storage)
  } catch {
    return current
  }
}

function safeOrganization(value: unknown): OfflineAuthContext['organization'] {
  if (!value || typeof value !== 'object') return null
  const row = value as Record<string, unknown>
  const id = safeText(row.id, 128)
  const name = safeText(row.name, 160)
  const slug = safeText(row.slug, 160)
  if (!id || !name || !slug) return null
  return {
    id, name, slug,
    kind: row.kind === 'team' ? 'team' : 'personal',
    plan: safeText(row.plan, 80),
  }
}

export function readOfflineAuthContext(storage: CacheStorage = localStorage): OfflineAuthContext | null {
  try {
    const raw = storage.getItem(tenantStorageKey(AUTH_CONTEXT_KEY, storage))
    if (!raw || raw.length > MAX_CACHE_BYTES) return null
    const parsed = JSON.parse(raw) as Record<string, unknown>
    if (parsed.version !== CACHE_VERSION) return null
    const updatedAt = safeTimestamp(parsed.updatedAt)
    if (!updatedAt) return null
    const organization = safeOrganization(parsed.organization)
    const organizations = Array.isArray(parsed.organizations)
      ? parsed.organizations.map(safeOrganization).filter((item): item is NonNullable<OfflineAuthContext['organization']> => item !== null).slice(0, 100)
      : []
    return {
      version: CACHE_VERSION,
      updatedAt,
      roles: Array.isArray(parsed.roles) ? parsed.roles.filter((value): value is string => typeof value === 'string').map(value => value.slice(0, 80)).slice(0, 50) : [],
      capabilities: Array.isArray(parsed.capabilities) ? parsed.capabilities.filter((value): value is string => typeof value === 'string').map(value => value.slice(0, 160)).slice(0, 500) : [],
      organization,
      organizations,
      is_platform_admin: parsed.is_platform_admin === true,
    }
  } catch { return null }
}

export function writeOfflineAuthContext(context: unknown, storage: Storage = localStorage): OfflineAuthContext | null {
  const source = context && typeof context === 'object' ? context as Record<string, unknown> : {}
  const candidate: OfflineAuthContext = {
    version: CACHE_VERSION,
    updatedAt: Date.now(),
    roles: Array.isArray(source.roles) ? source.roles.filter((value): value is string => typeof value === 'string').map(value => value.slice(0, 80)).slice(0, 50) : [],
    capabilities: Array.isArray(source.capabilities) ? source.capabilities.filter((value): value is string => typeof value === 'string').map(value => value.slice(0, 160)).slice(0, 500) : [],
    organization: safeOrganization(source.organization),
    organizations: Array.isArray(source.organizations)
      ? source.organizations.map(safeOrganization).filter((item): item is NonNullable<OfflineAuthContext['organization']> => item !== null).slice(0, 100)
      : [],
    is_platform_admin: source.is_platform_admin === true,
  }
  try {
    storage.setItem(tenantStorageKey(AUTH_CONTEXT_KEY, storage), JSON.stringify(candidate))
    return readOfflineAuthContext(storage)
  } catch {
    return null
  }
}
